import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import WindowDrawing, { TYPE_MAP } from '../components/WindowDrawing';

export default function ProfProjectDetail() {
    const { id } = useParams();

    const getToken = () => {
        return localStorage.getItem('pro_token') || sessionStorage.getItem('pro_token') || localStorage.getItem('admin_token') || sessionStorage.getItem('admin_token');
    };
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // PDF Upload State
    const [uploadingPdf, setUploadingPdf] = useState(false);
    const [pdfError, setPdfError] = useState('');
    
    // Manual Item State
    const [showItemModal, setShowItemModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [itemForm, setItemForm] = useState({
        title: '', model: '', quantity: 1, indexNum: '', location: '', width: '', height: '', glass: '', screen: '', shutter: '', color: '', perzol: '', notes: '', image: '', pageNum: ''
    });

    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [pricingMeta, setPricingMeta] = useState({ systems: [], glassOptions: [], colorOptions: [] });
    const [quoteData, setQuoteData] = useState(null);
    const [pricingParams, setPricingParams] = useState([]);
    const [activePanel, setActivePanel] = useState(null); // null | 'summary' | 'tips' | 'formula'
    const [settingsSaving, setSettingsSaving] = useState(false);

    // ═══ NEW: Selection, Pricing Cache, Reports ═══
    const [selectedItems, setSelectedItems] = useState(new Set());
    const [calcCache, setCalcCache] = useState({}); // { idx: { report, loading, error } }
    const [calcAllRunning, setCalcAllRunning] = useState(false);
    const [printMode, setPrintMode] = useState(null); // 'client' | 'materials' | 'assembly' | 'glass' | 'hardware'

    const API_BASE = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        fetchProject();
        fetchPricingMeta();
        fetchPricingSettings();
    }, [id]);

    const fetchPricingMeta = async () => {
        try {
            const token = getToken();
            // Fetch both pricing metadata AND glass catalog in parallel
            const [metaRes, glassRes] = await Promise.all([
                fetch(`${API_BASE}/api/pricing/metadata`, { headers: { 'Authorization': `Bearer ${token}` } }),
                fetch(`${API_BASE}/api/glass-catalog`)
            ]);
            const meta = metaRes.ok ? await metaRes.json() : { systems: [], glassOptions: [], colorOptions: [] };
            const glassCatalog = glassRes.ok ? await glassRes.json() : [];
            // Use GlassCatalog as the glass source (mapped to same format)
            if (Array.isArray(glassCatalog) && glassCatalog.length > 0) {
                meta.glassOptions = glassCatalog.map(g => ({
                    id: g.glassId,
                    description: g.description,
                    priceSqM: g.priceSqM,
                    thickness: g.thickness,
                }));
            }
            setPricingMeta(meta);
        } catch (err) { console.error("Pricing Meta Error", err); }
    };

    const fetchPricingSettings = async () => {
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/pricing/settings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setPricingParams(data.params || []);
            }
        } catch (err) { console.error("Settings Error", err); }
    };

    const savePricingSettings = async () => {
        setSettingsSaving(true);
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/pricing/settings`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ updates: pricingParams.map(p => ({ key: p.key, value: p.value })) })
            });
            if (res.ok) {
                alert('הגדרות התמחור עודכנו בהצלחה!');
            }
        } catch (err) { console.error("Settings Save Error", err); }
        setSettingsSaving(false);
    };

    const updateParam = (key, newValue) => {
        setPricingParams(prev => prev.map(p => p.key === key ? { ...p, value: parseFloat(newValue) || 0 } : p));
    };

    const fetchProject = async () => {
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/prof-projects/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('שגיאה בטעינת נתוני הפרויקט');
            const data = await res.json();
            setProject(data);
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePdfUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadingPdf(true);
        setPdfError('');
        const formData = new FormData();
        formData.append('pdfFile', file);

        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/prof-projects/${id}/parse-pdf`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            
            if (!res.ok) {
                const errData = await res.json();
                throw new Error(errData.error || 'שגיאה בעיבוד ה־PDF');
            }
            
            const updatedProject = await res.json();
            setProject(updatedProject);
        } catch (err) {
            console.error(err);
            setPdfError(err.message);
        } finally {
            setUploadingPdf(false);
            e.target.value = null; // reset input
        }
    };

    const handleSaveItem = async (e) => {
        e.preventDefault();
        try {
            let updatedItems = [...(project.items || [])];
            
            // Strip internal filter keys before saving
            const cleanItem = { ...itemForm };
            delete cleanItem._filterCat;
            delete cleanItem._filterSeries;
            delete cleanItem._filterWings;
            delete cleanItem._filterScreen;
            delete cleanItem._filterShutter;
            delete cleanItem._filterSearch;
            
            if (editingIndex !== null) {
                // Update existing item
                updatedItems[editingIndex] = cleanItem;
            } else {
                // Add new item
                updatedItems.push(cleanItem);
            }
            
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/prof-projects/${id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ items: updatedItems })
            });

            if (!res.ok) throw new Error('שגיאה בשמירת הפריט');
            
            const updatedProject = await res.json();
            setProject(updatedProject);
            closeItemModal();
        } catch (err) {
            console.error(err);
            alert(err.message);
        }
    };

    const handleDeleteItem = async (indexToDelete) => {
        if (!window.confirm('האם אתה בטוח שברצונך למחוק פריט זה?')) return;
        
        try {
            const updatedItems = project.items.filter((_, idx) => idx !== indexToDelete);
            const token = getToken();
            
            const res = await fetch(`${API_BASE}/api/prof-projects/${id}`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ items: updatedItems })
            });

            if (!res.ok) throw new Error('שגיאה במחיקת הפריט');
            
            // Clear stale caches before setting new project
            setSelectedItems(new Set());
            setCalcCache({});
            
            const updatedProject = await res.json();
            setProject(updatedProject);
        } catch (err) {
            console.error(err);
            alert(err.message);
        }
    };

    const openEditModal = (item, index) => {
        setItemForm(item);
        setEditingIndex(index);
        setShowItemModal(true);
    };

    const openAddModal = () => {
        setItemForm({ title: '', model: '', quantity: 1, indexNum: '', location: '', width: '', height: '', glass: '', screen: '', shutter: '', color: '', perzol: '', notes: '', image: '', pageNum: '' });
        setEditingIndex(null);
        setShowItemModal(true);
    };

    const closeItemModal = () => {
        setShowItemModal(false);
        setEditingIndex(null);
        setItemForm({ title: '', model: '', quantity: 1, indexNum: '', location: '', width: '', height: '', glass: '', screen: '', shutter: '', color: '', perzol: '', notes: '', image: '', pageNum: '' });
    };

    const handleCalculateItem = async (item) => {
        if (!item.model || !item.width || !item.height) {
            alert('אנא ודא כי דגם קליל, רוחב וגובה מוזנים על מנת לחשב הצעת מחיר (BoM).');
            return;
        }
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/pricing/calculate`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({
                    productCod: item.productCod || item.model,
                    width: item.width,
                    height: item.height,
                    glassId: item.glassId || null,
                    colorId: item.colorId || null,
                    glassDescription: item.glass || '',
                    glassThickness: (item.glass || '').includes('בידודית') || (item.glass || '').includes('14') ? '14' : '',
                    hasScreen: !!(item.screen && item.screen !== '') || (item.model || '').includes('רשת') || (item.title || '').includes('רשת')
                })
            });
            if (!res.ok) throw new Error('שגיאה בחישוב המערכת');
            const data = await res.json();
            setQuoteData({ item, report: data });
        } catch (err) {
            alert(err.message);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            const formData = new FormData();
            formData.append('image', file);
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/prof-projects/upload-image`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!res.ok) throw new Error('שגיאה בהעלאת התמונה');
            const data = await res.json();
            setItemForm({ ...itemForm, image: data.url });
        } catch (err) {
            console.error(err);
            alert(err.message);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // ═══ Cod → Sirtut lookup (for BASE drawing images) ═══
    const codToSirtut = useMemo(() => {
        const map = {};
        (pricingMeta?.systems || []).forEach(sys => {
            (sys.items || []).forEach(item => {
                if (item.cod && item.sirtut) map[item.cod] = item.sirtut;
            });
        });
        return map;
    }, [pricingMeta]);

    // Get BASE image path for an item (tries sirtut via productCod or model)
    const getItemImageSrc = useCallback((item) => {
        const cod = item.productCod || item.model;
        if (!cod) return null;
        const codNum = parseInt(cod);
        const sirtut = !isNaN(codNum) ? codToSirtut[codNum] : null;
        if (sirtut) return `/BASE/${sirtut}`;
        return null;
    }, [codToSirtut]);

    // Zoomed drawing state
    const [zoomedImage, setZoomedImage] = useState(null);

    const sortedItems = React.useMemo(() => {
        let sortableItems = [...(project?.items || [])];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                let aValue = a[sortConfig.key] || '';
                let bValue = b[sortConfig.key] || '';
                if (!isNaN(aValue) && !isNaN(bValue) && aValue !== '' && bValue !== '') {
                    aValue = Number(aValue); bValue = Number(bValue);
                }
                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [project, sortConfig]);

    // ═══ Price hash — detects if item details changed ═══
    const itemPriceHash = useCallback((item) => {
        return `${item.model||''}|${item.width||''}|${item.height||''}|${item.glass||''}|${item.color||''}|${item.productCod||''}|${item.glassId||''}|${item.colorId||''}`;
    }, []);

    // ═══ Load cached prices from DB on mount ═══
    useEffect(() => {
        if (!project?.items?.length) return;
        const initial = {};
        project.items.forEach((item, idx) => {
            if (item.cachedBom && item.cachedPrice != null) {
                const currentHash = itemPriceHash(item);
                if (item._priceHash === currentHash) {
                    // Item hasn't changed since last calc — use cached
                    initial[idx] = { loading: false, error: null, report: item.cachedBom };
                }
            }
        });
        if (Object.keys(initial).length > 0) {
            setCalcCache(prev => ({ ...prev, ...initial }));
        }
    }, [project?._id]); // intentionally only run on project load

    // ═══ Persist prices to DB ═══
    const persistPrices = useCallback(async (pricesToSave) => {
        if (!project?._id || pricesToSave.length === 0) return;
        try {
            const token = getToken();
            await fetch(`${API_BASE}/api/prof-projects/${project._id}/prices`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ prices: pricesToSave })
            });
        } catch (e) { /* silent — prices will just recalculate next time */ }
    }, [API_BASE, project?._id]);

    // ═══ Calculate single item ═══
    const calculateItem = useCallback(async (item, idx) => {
        if (!item.model || !item.width || !item.height) return;
        const hash = itemPriceHash(item);
        // Skip if already cached with same hash
        if (calcCache[idx]?.report && item._priceHash === hash) return;
        setCalcCache(prev => ({ ...prev, [idx]: { loading: true, error: null, report: prev[idx]?.report || null } }));
        try {
            const token = getToken();
            const res = await fetch(`${API_BASE}/api/pricing/calculate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    productCod: item.productCod || item.model,
                    width: item.width, height: item.height,
                    glassId: item.glassId || null, colorId: item.colorId || null,
                    glassDescription: item.glass || '',
                    glassThickness: (item.glass || '').includes('בידודית') || (item.glass || '').includes('14') ? '14' : '',
                    hasScreen: !!(item.screen && item.screen !== '') || (item.model || '').includes('רשת') || (item.title || '').includes('רשת')
                })
            });
            if (!res.ok) throw new Error('שגיאה בחישוב');
            const data = await res.json();
            setCalcCache(prev => ({ ...prev, [idx]: { loading: false, error: null, report: data } }));
            // Persist to DB
            persistPrices([{
                itemIndex: idx,
                cachedPrice: data.totals?.finalPrice || 0,
                cachedBom: data,
                _priceHash: hash
            }]);
        } catch (err) {
            setCalcCache(prev => ({ ...prev, [idx]: { loading: false, error: err.message, report: null } }));
        }
    }, [API_BASE, itemPriceHash, calcCache, persistPrices]);

    // ═══ Calculate all items ═══
    const calculateAll = useCallback(async () => {
        if (!project?.items?.length) return;
        setCalcAllRunning(true);
        const priceBatch = [];
        for (let i = 0; i < project.items.length; i++) {
            const item = project.items[i];
            const hash = itemPriceHash(item);
            if (item.model && item.width && item.height && !(calcCache[i]?.report && item._priceHash === hash)) {
                setCalcCache(prev => ({ ...prev, [i]: { loading: true, error: null, report: prev[i]?.report || null } }));
                try {
                    const token = getToken();
                    const res = await fetch(`${API_BASE}/api/pricing/calculate`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({
                            productCod: item.productCod || item.model,
                            width: item.width, height: item.height,
                            glassId: item.glassId || null, colorId: item.colorId || null,
                            glassDescription: item.glass || '',
                            glassThickness: (item.glass || '').includes('בידודית') || (item.glass || '').includes('14') ? '14' : '',
                            hasScreen: !!(item.screen && item.screen !== '') || (item.model || '').includes('רשת') || (item.title || '').includes('רשת')
                        })
                    });
                    if (!res.ok) throw new Error('שגיאה');
                    const data = await res.json();
                    setCalcCache(prev => ({ ...prev, [i]: { loading: false, error: null, report: data } }));
                    priceBatch.push({ itemIndex: i, cachedPrice: data.totals?.finalPrice || 0, cachedBom: data, _priceHash: hash });
                } catch (err) {
                    setCalcCache(prev => ({ ...prev, [i]: { loading: false, error: err.message, report: null } }));
                }
            }
        }
        // Batch persist all calculated prices
        if (priceBatch.length > 0) persistPrices(priceBatch);
        setCalcAllRunning(false);
    }, [project, calcCache, itemPriceHash, API_BASE, persistPrices]);

    // Auto-select all items when project loads
    useEffect(() => {
        if (project?.items?.length) {
            setSelectedItems(new Set(project.items.map((_, i) => i)));
        }
    }, [project?.items?.length]);

    // ═══ Selection helpers ═══
    const toggleSelectItem = (idx) => {
        setSelectedItems(prev => {
            const next = new Set(prev);
            if (next.has(idx)) next.delete(idx); else next.add(idx);
            return next;
        });
    };
    const toggleSelectAll = () => {
        const items = project?.items || [];
        if (selectedItems.size === items.length) setSelectedItems(new Set());
        else setSelectedItems(new Set(items.map((_, i) => i)));
    };

    // ═══ Pricing totals ═══
    const getItemUnitPrice = (idx) => calcCache[idx]?.report?.totals?.finalPrice || 0;
    const getItemTotal = (idx) => getItemUnitPrice(idx) * ((project?.items?.[idx]?.quantity) || 1);

    const projectTotalPrice = useMemo(() => {
        return (project?.items || []).reduce((sum, _, idx) => sum + getItemTotal(idx), 0);
    }, [project?.items, calcCache]);

    const selectedTotalPrice = useMemo(() => {
        return [...selectedItems].reduce((sum, idx) => sum + getItemTotal(idx), 0);
    }, [selectedItems, calcCache, project?.items]);

    // ═══ Aggregated data for reports ═══
    const getSelectedCalcData = () => {
        return [...selectedItems]
            .filter(idx => calcCache[idx]?.report)
            .map(idx => ({ idx, item: project.items[idx], report: calcCache[idx].report }));
    };

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
    );

    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50" dir="rtl">
            <div className="bg-red-50 text-red-600 p-6 rounded-xl font-bold shadow-lg max-w-md text-center">
                {error}
                <Link to="/professional/projects" className="block mt-4 text-sm underline">חזור לרשימת הפרויקטים</Link>
            </div>
        </div>
    );

    return (
        <div className="h-[calc(100vh-72px)] bg-slate-50 font-sans flex flex-col overflow-hidden" dir="rtl">
            {/* ═══ TOOLBAR (non-scrolling, always visible) ═══ */}
            <div className="shrink-0 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm no-print z-10">
                <div className="w-[98%] max-w-[1600px] mx-auto px-4">
                    {/* Top Row: Project Info + Back Link */}
                    <div className="flex items-center justify-between gap-4 pt-2.5 pb-1">
                        <div className="flex items-center gap-3 min-w-0">
                            <Link to="/professional/projects" className="shrink-0 text-slate-400 hover:text-blue-600 transition-colors p-1.5 hover:bg-slate-100 rounded-lg" title="חזרה לפרויקטים">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                            </Link>
                            <div className="min-w-0">
                                <h1 className="text-xl font-black text-slate-800 truncate">{project.title}</h1>
                                <div className="flex gap-3 text-[11px] font-medium text-slate-400">
                                    <span className="flex items-center gap-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        {project.clientName || '---'}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                        {new Date(project.createdAt).toLocaleDateString('he-IL')}
                                    </span>
                                    {(project?.items?.length > 0) && <span className="text-slate-400">• {project.items.length} פריטים</span>}
                                </div>
                            </div>
                        </div>
                        {/* Price Badge (right side of header) */}
                        <div className="flex items-center gap-3 shrink-0">
                            {(project?.items?.length > 0) && <span className="text-xs text-slate-500 font-bold hidden md:block">{selectedItems.size}/{project.items.length} נבחרו</span>}
                            {projectTotalPrice > 0 && (
                                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 px-3 py-1.5 rounded-xl">
                                    <span className="text-[10px] text-green-600 font-bold">סה"כ: </span>
                                    <span className="text-base font-black text-green-700">{projectTotalPrice.toLocaleString()} ₪</span>
                                </div>
                            )}
                        </div>
                    </div>
                    {pdfError && <div className="mx-4 mt-1 text-xs text-red-600 bg-red-50 p-2 rounded-lg font-bold border border-red-100">{pdfError}</div>}
                    {/* Bottom Row: Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-1.5 border-t border-slate-100 mt-1">
                        {/* Add Item + Import PDF */}
                        <label className="cursor-pointer bg-slate-800 hover:bg-slate-900 text-white px-3 py-1.5 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5 shadow-sm">
                            {uploadingPdf ? (
                                <span className="flex items-center gap-1.5">
                                    <div className="animate-spin h-3 w-3 border-2 border-white/40 border-t-white rounded-full" />
                                    מנתח...
                                </span>
                            ) : (
                                <>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                    ייבוא PDF
                                    <input type="file" accept="application/pdf" className="hidden" onChange={handlePdfUpload} disabled={uploadingPdf} />
                                </>
                            )}
                        </label>
                        <button onClick={openAddModal} className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-1.5 rounded-lg font-bold text-xs transition-colors flex items-center gap-1.5">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                            הוסף ידנית
                        </button>

                        <div className="h-6 w-px bg-slate-200" />

                        {/* Calculate All */}
                        <button 
                            onClick={calculateAll} 
                            disabled={calcAllRunning || !(project?.items?.length > 0)}
                            className="flex items-center gap-1.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-3 py-1.5 rounded-lg font-bold text-xs transition-all shadow-sm disabled:opacity-50"
                        >
                            {calcAllRunning ? (
                                <div className="animate-spin h-3 w-3 border-2 border-white/40 border-t-white rounded-full" />
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                            )}
                            {calcAllRunning ? 'מחשב...' : 'חשב הכל'}
                        </button>

                        <div className="h-6 w-px bg-slate-200" />

                        {/* Report Buttons */}
                        {[
                            { mode: 'client', label: 'דוח ללקוח', color: 'bg-blue-600 hover:bg-blue-700', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
                            { mode: 'materials', label: 'חומרים', color: 'bg-amber-600 hover:bg-amber-700', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
                            { mode: 'assembly', label: 'הרכבה', color: 'bg-teal-600 hover:bg-teal-700', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
                            { mode: 'glass', label: 'זכוכית', color: 'bg-cyan-600 hover:bg-cyan-700', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
                            { mode: 'hardware', label: 'פרזול', color: 'bg-purple-600 hover:bg-purple-700', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
                        ].map(btn => (
                            <button key={btn.mode} onClick={() => { setPrintMode(btn.mode); setTimeout(() => window.print(), 200); }}
                                disabled={!(project?.items?.length > 0)}
                                className={`flex items-center gap-1 ${btn.color} text-white px-2 py-1.5 rounded-lg font-bold text-[11px] transition-all shadow-sm disabled:opacity-40`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={btn.icon} /></svg>
                                {btn.label}
                            </button>
                        ))}
                        <div className="h-6 w-px bg-slate-200" />

                        {/* Panel Toggle Buttons */}
                        {[
                            { id: 'summary', label: 'סיכום AI', icon: 'M13 10V3L4 14h7v7l9-11h-7z', activeColor: 'bg-blue-600 text-white', inactiveColor: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
                            { id: 'tips', label: 'דגשים', icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z', activeColor: 'bg-amber-600 text-white', inactiveColor: 'bg-amber-50 text-amber-700 hover:bg-amber-100' },
                            { id: 'formula', label: 'נוסחה', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', activeColor: 'bg-orange-600 text-white', inactiveColor: 'bg-orange-50 text-orange-700 hover:bg-orange-100' },
                        ].map(p => (
                            <button key={p.id} onClick={() => setActivePanel(activePanel === p.id ? null : p.id)}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-lg font-bold text-[11px] transition-all ${activePanel === p.id ? p.activeColor + ' shadow-sm' : p.inactiveColor}`}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d={p.icon} /></svg>
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {/* ═══ Expandable Panels ═══ */}
                    {activePanel === 'summary' && (
                        <div className="border-t border-slate-100 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-b-2xl p-5 text-white">
                            <h3 className="font-bold text-sm mb-2 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                סיכום AI חכם
                            </h3>
                            <div className="text-blue-50 text-xs leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">
                                {project.pdfSummary || 'טרם עובד סיכום. העלה מפרט PDF ליצירת תקציר ותובנות אוטומטיות.'}
                            </div>
                        </div>
                    )}

                    {activePanel === 'tips' && (
                        <div className="border-t border-slate-100 bg-amber-50 rounded-b-2xl p-5">
                            <h3 className="font-bold text-sm text-amber-900 mb-2 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                                דגשים מיוחדים לחומרים
                            </h3>
                            {project.highlights && project.highlights.length > 0 ? (
                                <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                                    {project.highlights.map((hlt, i) => (
                                        <li key={i} className="flex gap-2 text-xs text-amber-800 font-medium leading-relaxed">
                                            <span className="shrink-0 w-1 h-1 rounded-full bg-amber-400 mt-1.5"></span>
                                            {hlt}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-xs text-amber-700/60 font-medium">אין דגשים כרגע.</div>
                            )}
                        </div>
                    )}

                    {activePanel === 'formula' && (
                        <div className="border-t border-slate-100 bg-white rounded-b-2xl p-5">
                            {/* Formula Visual */}
                            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-3 mb-4 border border-slate-200">
                                <p className="text-[10px] font-bold text-slate-500 mb-1">נוסחת התמחור:</p>
                                <p className="text-xs font-mono text-slate-700 leading-relaxed" dir="ltr">
                                    <span className="text-blue-600">Final Price</span> = (<span className="text-green-600">Materials</span> + <span className="text-orange-600">Assembly</span>) × (1 + <span className="text-purple-600">Install%</span>) × <span className="text-red-600">FAKTOR</span>
                                </p>
                                <p className="text-[10px] text-slate-400 mt-1">חומרים = (אלומיניום לפי משקל × מחיר/ק"ג לפי צבע) + זכוכית + פרזול</p>
                            </div>
                            {/* Editable Parameters Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {pricingParams.map(param => (
                                    <div key={param.key} className="bg-white border border-slate-200 rounded-lg p-3 hover:shadow-md transition-shadow">
                                        <label className="block text-[10px] font-bold text-slate-500 mb-1">{param.label}</label>
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="number"
                                                step="any"
                                                value={param.value}
                                                onChange={e => updateParam(param.key, e.target.value)}
                                                className="flex-1 p-1.5 rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-amber-500 outline-none text-sm font-bold text-slate-800 text-center"
                                            />
                                            <span className="text-[10px] font-bold text-slate-400">{param.unit}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-end mt-4">
                                <button
                                    onClick={savePricingSettings}
                                    disabled={settingsSaving}
                                    className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-5 py-2 rounded-xl font-bold text-sm shadow-md hover:shadow-lg transition-all disabled:opacity-50 flex items-center gap-2"
                                >
                                    {settingsSaving ? <span className="animate-spin">⏳</span> : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                                    )}
                                    שמור
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ═══ SCROLLABLE CONTENT ═══ */}
            <div className="flex-1 overflow-y-auto">
            <div className="w-[98%] max-w-[1600px] mx-auto px-4 py-4 pb-20">

                {/* Print Styles */}
                <style>{`
                    @media print {
                        @page { margin: 12mm 10mm; }
                        body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        .no-print { display: none !important; }
                        .print-only { display: block !important; }
                    }
                    .print-only { display: none; }
                `}</style>

                {/* Main Content - Items Table */}
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden no-print">
                    <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                        <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">
                            רשימת פריטים ({sortedItems.length})
                        </h2>
                    </div>
                    
                    <div className="overflow-x-auto">
                        <table className="w-full text-right text-sm">
                            <thead className="bg-slate-50 text-slate-500 font-bold border-b border-slate-100 whitespace-nowrap select-none">
                                <tr>
                                    {/* Select All Checkbox */}
                                    <th className="p-4 w-10 text-center">
                                        <input type="checkbox" checked={selectedItems.size === (project?.items || []).length && selectedItems.size > 0} onChange={toggleSelectAll} className="w-4 h-4 cursor-pointer accent-blue-600" />
                                    </th>
                                    {/* Sortable Headers */}
                                    {[
                                        { key: 'indexNum', label: 'אינדקס', width: 'w-14' },
                                        { key: 'title', label: 'תיאור', width: 'w-40' },
                                        { key: 'model', label: 'דגם', width: 'w-18' },
                                        { key: 'quantity', label: 'כמות', width: 'w-14' },
                                        { key: 'width', label: 'מידות', width: 'w-20' },
                                        { key: 'location', label: 'מיקום', width: 'w-20' },
                                        { key: 'glass', label: 'זכוכית', width: 'w-20' },
                                        { key: 'shutter', label: 'תריס', width: 'w-20' },
                                        { key: 'color', label: 'צבע', width: 'w-18' },
                                    ].map(col => (
                                        <th key={col.key} className={`p-3 ${col.width} hover:bg-slate-100 cursor-pointer transition-colors`} onClick={() => handleSort(col.key)}>
                                            <div className={`flex items-center gap-1 ${['indexNum', 'model', 'quantity', 'width'].includes(col.key) ? 'justify-center' : ''}`}>
                                                {col.label}
                                                {sortConfig.key === col.key && (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 transition-transform ${sortConfig.direction === 'desc' ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                    <th className="p-3 w-24 text-center text-green-600">מחיר/יח'</th>
                                    <th className="p-3 w-24 text-center text-green-700">סה"כ</th>
                                    <th className="p-3 w-16 text-center text-slate-400">שרטוט</th>
                                    <th className="p-3 w-12 text-center text-slate-400">PDF</th>
                                    <th className="p-3 w-16 text-center text-slate-400">פעולות</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {sortedItems.length > 0 ? (
                                    sortedItems.map((item, idx) => {
                                        const originalIdx = project.items.findIndex(i => i === item);
                                        const cc = calcCache[originalIdx];
                                        const unitPrice = getItemUnitPrice(originalIdx);
                                        const total = getItemTotal(originalIdx);
                                        return (
                                            <tr key={idx} className={`hover:bg-slate-50/50 transition-colors group ${selectedItems.has(originalIdx) ? 'bg-blue-50/30' : ''}`}>
                                                {/* Checkbox */}
                                                <td className="p-3 text-center">
                                                    <input type="checkbox" checked={selectedItems.has(originalIdx)} onChange={() => toggleSelectItem(originalIdx)} className="w-4 h-4 cursor-pointer accent-blue-600" />
                                                </td>
                                                <td className="p-3 font-bold text-slate-700 text-center text-xs">{item.indexNum || '-'}</td>
                                                <td className="p-3 font-bold text-slate-800 text-xs">
                                                    {item.title}
                                                    {item.notes && <p className="text-[10px] text-slate-400 font-normal mt-0.5">{item.notes}</p>}
                                                </td>
                                                <td className="p-3 text-center text-slate-600 font-bold" dir="ltr">
                                                    {item.model ? <span className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded text-[10px]">{item.model}</span> : '---'}
                                                </td>
                                                <td className="p-3 text-center font-black text-blue-900 bg-blue-50/30 text-xs">{item.quantity || 1}</td>
                                                <td className="p-3 text-center text-slate-600 text-xs" dir="ltr">
                                                    {(item.width || item.height) ? `${item.width || '-'} x ${item.height || '-'}` : '---'}
                                                </td>
                                                <td className="p-3 text-slate-600 text-[10px]">{item.location || '-'}</td>
                                                <td className="p-3 text-slate-600 text-[10px]">{item.glass || '-'}</td>
                                                <td className="p-3 text-slate-600 text-[10px]">{item.shutter || '-'}</td>
                                                <td className="p-3 text-slate-600 text-[10px]">
                                                    {item.color ? <span className="inline-block px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">{item.color}</span> : '---'}
                                                </td>
                                                {/* Unit Price */}
                                                <td className="p-3 text-center">
                                                    {cc?.loading ? (
                                                        <div className="animate-spin h-4 w-4 border-2 border-green-400 border-t-transparent rounded-full mx-auto" />
                                                    ) : cc?.error ? (
                                                        <span className="text-red-400 text-[10px]">⚠ שגיאה</span>
                                                    ) : unitPrice > 0 ? (
                                                        <span className="font-bold text-green-700 text-xs">{unitPrice.toLocaleString()} ₪</span>
                                                    ) : (
                                                        <button onClick={() => calculateItem(item, originalIdx)} className="text-[10px] text-slate-400 hover:text-green-600 border border-slate-200 px-2 py-1 rounded-lg hover:bg-green-50 transition-colors">חשב</button>
                                                    )}
                                                </td>
                                                {/* Total */}
                                                <td className="p-3 text-center">
                                                    {total > 0 ? (
                                                        <span className="font-black text-green-800 text-xs bg-green-50 px-2 py-1 rounded-lg">{total.toLocaleString()} ₪</span>
                                                    ) : (
                                                        <span className="text-slate-300 text-xs">—</span>
                                                    )}
                                                </td>
                                                {/* Drawing Thumbnail */}
                                                <td className="p-2 text-center">
                                                    {(() => {
                                                        const imgSrc = getItemImageSrc(item);
                                                        if (imgSrc) {
                                                            return (
                                                                <div className="cursor-pointer group/img" onClick={() => setZoomedImage(imgSrc)}>
                                                                    <img
                                                                        src={imgSrc + '.JPG'}
                                                                        alt="שרטוט"
                                                                        className="w-12 h-12 object-contain mx-auto rounded border border-slate-200 bg-white p-0.5 group-hover/img:border-blue-400 group-hover/img:shadow-md transition-all"
                                                                        onError={(e) => { e.target.onerror = null; e.target.src = imgSrc + '.jpg'; }}
                                                                    />
                                                                </div>
                                                            );
                                                        }
                                                        if (item.image) return (
                                                            <img src={item.image} alt="" className="w-10 h-10 object-contain mx-auto rounded border border-slate-200 bg-white p-0.5 cursor-pointer" onClick={() => setZoomedImage(item.image)} />
                                                        );
                                                        return <span className="text-slate-300">—</span>;
                                                    })()}
                                                </td>
                                                {/* PDF Reference */}
                                                <td className="p-2 text-center">
                                                    {item.pageNum && project.originalPdf ? (
                                                        <a href={`${project.originalPdf}#page=${item.pageNum}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-red-500 hover:text-red-700 transition-colors" title={`עמוד ${item.pageNum}`}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/><text x="7" y="18" fontSize="6" fontWeight="bold" fill="currentColor">PDF</text></svg>
                                                            <span className="text-[9px] font-bold">{item.pageNum}</span>
                                                        </a>
                                                    ) : (
                                                        <span className="text-slate-300">—</span>
                                                    )}
                                                </td>
                                                {/* Actions */}
                                                <td className="p-3 text-center align-middle">
                                                    <div className="flex justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button onClick={() => { calculateItem(item, originalIdx); }} className="text-slate-400 hover:text-green-600 p-0.5" title="חשב מחיר">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                                        </button>
                                                        <button onClick={() => { handleCalculateItem(item); }} className="text-slate-400 hover:text-indigo-600 p-0.5" title="הצג פירוט">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                                        </button>
                                                        <button onClick={() => openEditModal(item, originalIdx)} className="text-slate-400 hover:text-blue-600 p-0.5" title="ערוך">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                        </button>
                                                        <button onClick={() => handleDeleteItem(originalIdx)} className="text-slate-400 hover:text-red-600 p-0.5" title="מחק">
                                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="15" className="p-8 text-center text-slate-400 font-medium">
                                            אין פריטים עדיין. <button onClick={openAddModal} className="text-blue-500 hover:underline mx-1">הוסף ידנית</button> או העלה PDF.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {/* Table Footer Total */}
                            {projectTotalPrice > 0 && (
                                <tfoot>
                                    <tr className="bg-gradient-to-l from-green-50 to-white border-t-2 border-green-200">
                                        <td colSpan="12" className="p-4 text-left font-black text-green-800 text-lg">סה"כ פרויקט</td>
                                        <td className="p-4 text-center font-black text-green-800 text-lg">{projectTotalPrice.toLocaleString()} ₪</td>
                                        <td colSpan="3"></td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                {/* ═══ Drawing Lightbox ═══ */}
                {zoomedImage && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center no-print" onClick={() => setZoomedImage(null)}>
                        <div className="relative bg-white rounded-2xl shadow-2xl p-4 max-w-lg max-h-[80vh]" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setZoomedImage(null)} className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-500 hover:text-red-500 transition-colors text-lg font-bold">✕</button>
                            <img
                                src={zoomedImage + '.JPG'}
                                alt="שרטוט מוגדל"
                                className="max-w-full max-h-[70vh] object-contain mx-auto"
                                onError={(e) => { e.target.onerror = null; e.target.src = zoomedImage + '.jpg'; }}
                            />
                        </div>
                    </div>
                )}

                {/* ═══════════════════════════════════════════════════════
                    PRINT-ONLY REPORTS
                    ═══════════════════════════════════════════════════════ */}
                
                {/* Shared Print Header */}
                {(() => {
                    const PrintHeader = ({ title }) => (
                        <div style={{ borderBottom: '3px solid #1e3a5f', paddingBottom: '10px', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <img src="/assets/logo-circle.png" alt="Logo" style={{ width: '55px', height: '55px', objectFit: 'contain' }} />
                                    <div>
                                        <h1 style={{ fontSize: '18pt', fontWeight: 'bold', color: '#1e3a5f', margin: 0 }}>אלום נואטחה בע"מ</h1>
                                        <p style={{ fontSize: '8pt', color: '#888', margin: 0 }}>ALUM NAWATHA LTD | טל: 052-878-4031</p>
                                    </div>
                                </div>
                                <div style={{ textAlign: 'left', fontSize: '9pt', color: '#555' }}>
                                    <div><b>תאריך:</b> {new Date().toLocaleDateString('he-IL')}</div>
                                    <div><b>פרויקט:</b> {project?.title || 'פרויקט'}</div>
                                    <div><b>לקוח:</b> {project?.clientName || '---'}</div>
                                    <div><b>פריטים:</b> {selectedItems.size}</div>
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '14pt', fontWeight: 'bold', color: '#1e3a5f' }}>{title}</div>
                        </div>
                    );

                    const selectedData = getSelectedCalcData();

                    return (
                        <>
                        {/* CLIENT REPORT */}
                        <div className={`print-only ${printMode === 'client' ? '' : 'hidden'}`} dir="rtl" style={{ fontSize: '11pt' }}>
                            <PrintHeader title="הצעת מחיר ללקוח" />
                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right' }}>
                                <thead>
                                    <tr style={{ background: '#e8f0fe', borderBottom: '2px solid #1e3a5f' }}>
                                        <th style={{ padding: '8px', borderLeft: '1px solid #ccc' }}>#</th>
                                        <th style={{ padding: '8px', borderLeft: '1px solid #ccc' }}>תיאור</th>
                                        <th style={{ padding: '8px', borderLeft: '1px solid #ccc' }}>מידות</th>
                                        <th style={{ padding: '8px', borderLeft: '1px solid #ccc' }}>כמות</th>
                                        <th style={{ padding: '8px', borderLeft: '1px solid #ccc' }}>מחיר ליחידה</th>
                                        <th style={{ padding: '8px' }}>סה"כ</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...selectedItems].sort((a,b)=>a-b).filter(idx => project.items[idx]).map((idx, i) => {
                                        const item = project.items[idx];
                                        const up = getItemUnitPrice(idx);
                                        const tot = getItemTotal(idx);
                                        return (
                                            <tr key={idx} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                <td style={{ padding: '6px 8px', borderLeft: '1px solid #e8e8e8', textAlign: 'center', color: '#666' }}>{i + 1}</td>
                                                <td style={{ padding: '6px 8px', borderLeft: '1px solid #e8e8e8', fontWeight: 'bold' }}>{item.title || ''}</td>
                                                <td style={{ padding: '6px 8px', borderLeft: '1px solid #e8e8e8', textAlign: 'center', fontFamily: 'monospace' }}>{item.width && item.height ? `${item.width}×${item.height}` : '—'}</td>
                                                <td style={{ padding: '6px 8px', borderLeft: '1px solid #e8e8e8', textAlign: 'center', fontWeight: 'bold' }}>{item.quantity || 1}</td>
                                                <td style={{ padding: '6px 8px', borderLeft: '1px solid #e8e8e8', textAlign: 'center' }}>{up > 0 ? `${up.toLocaleString()} ₪` : '—'}</td>
                                                <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 'bold', color: '#1e3a5f' }}>{tot > 0 ? `${tot.toLocaleString()} ₪` : '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr style={{ borderTop: '3px solid #1e3a5f', background: '#f0f4ff' }}>
                                        <td colSpan="5" style={{ padding: '10px', fontWeight: 'bold', fontSize: '13pt', color: '#1e3a5f' }}>סה"כ</td>
                                        <td style={{ padding: '10px', fontWeight: 'bold', fontSize: '13pt', color: '#1e3a5f', textAlign: 'center' }}>{selectedTotalPrice.toLocaleString()} ₪</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {/* MATERIALS REPORT */}
                        <div className={`print-only ${printMode === 'materials' ? '' : 'hidden'}`} dir="rtl" style={{ fontSize: '10pt' }}>
                            <PrintHeader title="דוח חומרים — פרופילי אלומיניום" />
                            {selectedData.map(({ idx, item, report }) => (
                                <div key={idx} style={{ marginBottom: '16px', pageBreakInside: 'avoid' }}>
                                    <div style={{ background: '#e8f0fe', padding: '6px 10px', borderRadius: '6px', fontWeight: 'bold', color: '#1e3a5f', marginBottom: '6px' }}>
                                        #{idx + 1} {item.title} — {item.width}×{item.height} × {item.quantity || 1}
                                    </div>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                                        <thead><tr style={{ background: '#f5f5f5', borderBottom: '1px solid #ddd' }}>
                                            <th style={{ padding: '4px 6px', textAlign: 'right' }}>פרופיל</th>
                                            <th style={{ padding: '4px 6px', textAlign: 'center' }}>מקט</th>
                                            <th style={{ padding: '4px 6px', textAlign: 'center' }}>חיתוך (מ"מ)</th>
                                            <th style={{ padding: '4px 6px', textAlign: 'center' }}>כמות</th>
                                            <th style={{ padding: '4px 6px', textAlign: 'center' }}>משקל (ק"ג)</th>
                                        </tr></thead>
                                        <tbody>
                                            {report.bom.filter(b => b.type === 'profile').map((b, i) => (
                                                <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '3px 6px' }}>{b.name}</td>
                                                    <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace' }}>{b.makat}</td>
                                                    <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace', color: '#c00' }}>{b.lengthMm}</td>
                                                    <td style={{ padding: '3px 6px', textAlign: 'center', fontWeight: 'bold' }}>{b.qty}</td>
                                                    <td style={{ padding: '3px 6px', textAlign: 'center' }}>{(b.weightGr / 1000).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                            <div style={{ borderTop: '2px solid #1e3a5f', padding: '8px', fontWeight: 'bold', fontSize: '11pt', color: '#1e3a5f', marginTop: '10px' }}>
                                סה"כ משקל אלומיניום: {selectedData.reduce((sum, d) => sum + (d.report.totals?.aluminumWeightKg || 0) * (d.item.quantity || 1), 0).toFixed(2)} ק"ג
                            </div>
                        </div>

                        {/* ASSEMBLY REPORT */}
                        <div className={`print-only ${printMode === 'assembly' ? '' : 'hidden'}`} dir="rtl" style={{ fontSize: '10pt' }}>
                            <PrintHeader title="דוח הרכבה — מידות חיתוך" />
                            {selectedData.map(({ idx, item, report }) => (
                                <div key={idx} style={{ marginBottom: '16px', pageBreakInside: 'avoid' }}>
                                    <div style={{ background: '#e0f2f1', padding: '6px 10px', borderRadius: '6px', fontWeight: 'bold', color: '#00695c', marginBottom: '6px' }}>
                                        #{idx + 1} {item.title} — {item.width}×{item.height} | {report.product?.description}
                                    </div>
                                    {report.bom.filter(b => b.type === 'dimension').length > 0 && (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt', marginBottom: '8px' }}>
                                            <thead><tr style={{ background: '#f5f5f5' }}>
                                                <th style={{ padding: '4px 6px', textAlign: 'right' }}>חלק</th>
                                                <th style={{ padding: '4px 6px', textAlign: 'center' }}>חיתוך (מ"מ)</th>
                                                <th style={{ padding: '4px 6px', textAlign: 'center' }}>כמות</th>
                                            </tr></thead>
                                            <tbody>
                                                {report.bom.filter(b => b.type === 'dimension' || b.type === 'profile').map((b, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                        <td style={{ padding: '3px 6px' }}>{b.name} {b.makat ? `(${b.makat})` : ''}</td>
                                                        <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 'bold', color: '#c00' }}>{b.lengthMm}</td>
                                                        <td style={{ padding: '3px 6px', textAlign: 'center' }}>{b.qty}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* GLASS REPORT */}
                        <div className={`print-only ${printMode === 'glass' ? '' : 'hidden'}`} dir="rtl" style={{ fontSize: '10pt' }}>
                            <PrintHeader title="הזמנת זכוכית" />
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr style={{ background: '#e0f7fa', borderBottom: '2px solid #0097a7' }}>
                                    <th style={{ padding: '6px 8px' }}>#</th>
                                    <th style={{ padding: '6px 8px' }}>פריט</th>
                                    <th style={{ padding: '6px 8px' }}>סוג זכוכית</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>רוחב (מ"מ)</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>גובה (מ"מ)</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>כנפיים</th>
                                    <th style={{ padding: '6px 8px', textAlign: 'center' }}>כמות × יח'</th>
                                </tr></thead>
                                <tbody>
                                    {selectedData.flatMap(({ idx, item, report }) => 
                                        report.bom.filter(b => b.type === 'glass').map((g, i) => (
                                            <tr key={`${idx}-${i}`} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                                <td style={{ padding: '5px 8px', textAlign: 'center', color: '#666' }}>{idx + 1}</td>
                                                <td style={{ padding: '5px 8px', fontWeight: 'bold' }}>{item.title}</td>
                                                <td style={{ padding: '5px 8px' }}>{g.name}</td>
                                                <td style={{ padding: '5px 8px', fontFamily: 'monospace', textAlign: 'center', fontWeight: 'bold', color: '#c00' }}>{g.glassWidthMm}</td>
                                                <td style={{ padding: '5px 8px', fontFamily: 'monospace', textAlign: 'center', fontWeight: 'bold', color: '#c00' }}>{g.glassHeightMm}</td>
                                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>{g.qtyWings}</td>
                                                <td style={{ padding: '5px 8px', textAlign: 'center', fontWeight: 'bold' }}>{g.qtyWings * (item.quantity || 1)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* HARDWARE REPORT */}
                        <div className={`print-only ${printMode === 'hardware' ? '' : 'hidden'}`} dir="rtl" style={{ fontSize: '10pt' }}>
                            <PrintHeader title="דוח פרזול / חומרה" />
                            {selectedData.map(({ idx, item, report }) => {
                                const hw = report.bom.filter(b => b.type === 'hardware');
                                if (hw.length === 0) return null;
                                return (
                                    <div key={idx} style={{ marginBottom: '14px', pageBreakInside: 'avoid' }}>
                                        <div style={{ background: '#fef3c7', padding: '5px 10px', borderRadius: '6px', fontWeight: 'bold', color: '#92400e', marginBottom: '4px' }}>
                                            #{idx + 1} {item.title} × {item.quantity || 1}
                                        </div>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9pt' }}>
                                            <thead><tr style={{ background: '#fffbeb', borderBottom: '1px solid #ddd' }}>
                                                <th style={{ padding: '3px 6px', textAlign: 'right' }}>פרזול</th>
                                                <th style={{ padding: '3px 6px', textAlign: 'center' }}>מקט</th>
                                                <th style={{ padding: '3px 6px', textAlign: 'center' }}>כמות</th>
                                                <th style={{ padding: '3px 6px', textAlign: 'center' }}>מחיר/יח'</th>
                                                <th style={{ padding: '3px 6px', textAlign: 'center' }}>סה"כ</th>
                                            </tr></thead>
                                            <tbody>
                                                {hw.map((h, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                                                        <td style={{ padding: '3px 6px' }}>{h.name}</td>
                                                        <td style={{ padding: '3px 6px', textAlign: 'center', fontFamily: 'monospace' }}>{h.pnum || '—'}</td>
                                                        <td style={{ padding: '3px 6px', textAlign: 'center' }}>{h.qty} {h.unit}</td>
                                                        <td style={{ padding: '3px 6px', textAlign: 'center' }}>{h.unitPrice}₪</td>
                                                        <td style={{ padding: '3px 6px', textAlign: 'center', fontWeight: 'bold' }}>{h.cost}₪</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                );
                            })}
                            <div style={{ borderTop: '2px solid #92400e', padding: '8px', fontWeight: 'bold', fontSize: '11pt', color: '#92400e', marginTop: '10px' }}>
                                סה"כ פרזול: {selectedData.reduce((sum, d) => sum + (d.report.totals?.hardwareCost || 0) * (d.item.quantity || 1), 0).toLocaleString()} ₪
                            </div>
                        </div>
                        </>
                    );
                })()}

            </div>

            {/* Add/Edit Item Modal - Full Screen Wizard */}
            {showItemModal && (() => {
                // ========== SMART FILTERS ==========
                const allProducts = pricingMeta.systems.flatMap(s => s.items.map(i => ({ ...i, manufacturer: s.manufacturer, series: s.series })));
                
                // Extract unique filter values
                const uniqueManufacturers = [...new Set(allProducts.map(p => p.manufacturer).filter(Boolean))].sort();
                
                // Build filter state from item context
                const filterMfr = itemForm._filterMfr || '';
                const filterSeries = itemForm._filterSeries || '';
                const filterCat = itemForm._filterCat || '';
                const filterWings = itemForm._filterWings || '';
                const filterScreen = itemForm._filterScreen || '';
                const filterShutter = itemForm._filterShutter || '';
                const filterFixed = itemForm._filterFixed || '';
                const filterSearch = itemForm._filterSearch || '';
                
                // Series for selected manufacturer
                const mfrProducts = filterMfr ? allProducts.filter(p => p.manufacturer === filterMfr) : allProducts;
                const uniqueSeries = [...new Set(mfrProducts.map(p => p.series).filter(Boolean))].sort();
                
                // Products for selected series
                const seriesProducts = filterSeries ? mfrProducts.filter(p => p.series === filterSeries) : mfrProducts;
                const uniqueCategories = [...new Set(seriesProducts.map(p => p.openingType).filter(Boolean))];
                const uniqueWings = [...new Set(seriesProducts.map(p => p.wingCount).filter(w => w > 0))].sort((a,b) => a-b);
                
                // Apply all filters to get final product list
                let filteredProducts = seriesProducts;
                if (filterCat) filteredProducts = filteredProducts.filter(p => p.openingType === filterCat);
                if (filterWings) filteredProducts = filteredProducts.filter(p => p.wingCount === parseInt(filterWings));
                if (filterScreen === 'yes') filteredProducts = filteredProducts.filter(p => (p.description || '').includes('רשת'));
                if (filterScreen === 'no') filteredProducts = filteredProducts.filter(p => !(p.description || '').includes('רשת'));
                if (filterShutter === 'yes') filteredProducts = filteredProducts.filter(p => (p.description || '').includes('תריס') || (p.description || '').includes('מונו'));
                if (filterShutter === 'no') filteredProducts = filteredProducts.filter(p => !(p.description || '').includes('תריס') && !(p.description || '').includes('מונו'));
                if (filterFixed === 'yes') filteredProducts = filteredProducts.filter(p => (p.description || '').includes('אמבטיה') || (p.description || '').includes('קבוע') || (p.fixedType && p.fixedType !== ''));
                if (filterFixed === 'no') filteredProducts = filteredProducts.filter(p => !(p.description || '').includes('אמבטיה') && !(p.description || '').includes('קבוע'));
                if (filterSearch) {
                    const q = filterSearch.toLowerCase();
                    filteredProducts = filteredProducts.filter(p => (p.description || '').toLowerCase().includes(q) || String(p.cod).includes(q));
                }
                
                // Score relevance
                const titleLower = (itemForm.title || '').toLowerCase();
                const scoreProduct = (p) => {
                    let score = 0;
                    const desc = (p.description || '').toLowerCase();
                    if (titleLower.includes('רשת') && desc.includes('רשת')) score += 3;
                    if (titleLower.includes('תריס') && desc.includes('תריס')) score += 3;
                    if (p.cod === parseInt(itemForm.productCod)) score += 100;
                    return score;
                };
                
                filteredProducts = filteredProducts.sort((a, b) => scoreProduct(b) - scoreProduct(a));
                const selectedCod = itemForm.productCod ? String(itemForm.productCod) : '';
                
                // Wizard step determination
                const wizardStep = !filterMfr ? 1 : !filterSeries ? 2 : 3;
                
                return (
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-50 flex items-stretch justify-center p-3 md:p-6">
                    <div className="bg-white rounded-3xl w-full max-w-6xl shadow-2xl flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-gradient-to-l from-blue-50 to-white flex-shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-black text-slate-800">
                                        {editingIndex !== null ? 'עריכת פריט' : 'הוספת פריט חדש'}
                                    </h2>
                                    <p className="text-xs text-slate-400">בחר מוצר מהקטלוג ומלא את פרטי הפריט</p>
                                </div>
                            </div>
                            <button type="button" onClick={closeItemModal} className="text-slate-400 hover:bg-slate-100 w-10 h-10 rounded-xl flex items-center justify-center transition-colors text-xl font-bold">
                                &times;
                            </button>
                        </div>

                        {/* Two Panel Layout */}
                        <form onSubmit={handleSaveItem} className="flex flex-col md:flex-row flex-1 overflow-hidden">
                            {/* LEFT PANEL - Product Wizard */}
                            <div className="w-full md:w-1/2 border-l border-slate-100 flex flex-col bg-slate-50/50">
                                {/* Search Bar */}
                                <div className="p-4 border-b border-slate-100">
                                    <div className="relative">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <input
                                            type="text"
                                            value={filterSearch}
                                            onChange={e => setItemForm({...itemForm, _filterSearch: e.target.value})}
                                            className="w-full pr-10 pl-4 py-2.5 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                                            placeholder="חפש מוצר לפי שם או מקט..."
                                        />
                                    </div>
                                    {/* Breadcrumb navigation */}
                                    {(filterMfr || filterSearch) && (
                                        <div className="flex items-center gap-1 mt-2 text-[10px] font-bold flex-wrap">
                                            <button type="button" onClick={() => setItemForm({...itemForm, _filterMfr: '', _filterSeries: '', _filterCat: '', _filterWings: '', _filterScreen: '', _filterShutter: '', _filterFixed: '', _filterSearch: ''})} className="text-blue-600 hover:underline">הכל</button>
                                            {filterMfr && (
                                                <>
                                                    <span className="text-slate-300">›</span>
                                                    <button type="button" onClick={() => setItemForm({...itemForm, _filterSeries: '', _filterCat: '', _filterWings: '', _filterScreen: '', _filterShutter: '', _filterFixed: ''})} className="text-blue-600 hover:underline">{filterMfr}</button>
                                                </>
                                            )}
                                            {filterSeries && (
                                                <>
                                                    <span className="text-slate-300">›</span>
                                                    <span className="text-slate-700">{filterSeries}</span>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Direct search results */}
                                {filterSearch ? (
                                    <div className="flex-1 overflow-y-auto p-3 custom-scrollbar">
                                        <p className="text-[10px] text-slate-400 font-bold mb-2">{filteredProducts.length} תוצאות חיפוש</p>
                                        <div className="space-y-1">
                                            {filteredProducts.map(p => (
                                                <button
                                                    type="button"
                                                    key={p.cod}
                                                    onClick={() => setItemForm({...itemForm, productCod: String(p.cod), model: p.description, _filterSearch: ''})}
                                                    className={`w-full text-right p-3 rounded-xl text-sm transition-all ${
                                                        selectedCod === String(p.cod) 
                                                            ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' 
                                                            : 'bg-white hover:bg-blue-50 text-slate-700 border border-slate-100 hover:border-blue-200'
                                                    }`}
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <div className="flex items-center gap-1.5">
                                                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-slate-100 text-slate-400'}`}>{p.cod}</span>
                                                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-indigo-50 text-indigo-600'}`}>{p.manufacturer} • {p.series}</span>
                                                        </div>
                                                        <span className={`text-[10px] ${selectedCod === String(p.cod) ? 'text-blue-200' : 'text-slate-400'}`}>{p.openingType}</span>
                                                    </div>
                                                    <p className="font-bold mt-1 text-right leading-tight">{p.description}</p>
                                                </button>
                                            ))}
                                            {filteredProducts.length === 0 && (
                                                <div className="text-center py-8 text-slate-400">
                                                    <p className="text-sm font-bold">לא נמצאו מוצרים</p>
                                                    <p className="text-xs mt-1">נסה לשנות את החיפוש</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : wizardStep === 1 ? (
                                    /* ═══ STEP 1: Choose Company ═══ */
                                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">שלב 1: בחר יצרן ({uniqueManufacturers.length})</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {uniqueManufacturers.map(mfr => {
                                                const mfrCount = allProducts.filter(p => p.manufacturer === mfr).length;
                                                const mfrSeriesCount = new Set(allProducts.filter(p => p.manufacturer === mfr).map(p => p.series)).size;
                                                const icons = { 'קליל': '🏗️', 'אקסטל': '🔧', 'אלובין': '⚙️', 'אלומינה': '🔩', 'שרמר': '🪟', 'ארפל': '🛡️' };
                                                return (
                                                    <button
                                                        type="button"
                                                        key={mfr}
                                                        onClick={() => setItemForm({...itemForm, _filterMfr: mfr})}
                                                        className="text-right p-4 rounded-2xl transition-all border bg-white border-slate-100 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md group"
                                                    >
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className="text-lg">{icons[mfr] || '🏭'}</span>
                                                            <p className="font-black text-slate-800 text-base group-hover:text-blue-700">{mfr}</p>
                                                        </div>
                                                        <div className="flex gap-2 mt-1">
                                                            <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{mfrSeriesCount} סדרות</span>
                                                            <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{mfrCount} מוצרים</span>
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : wizardStep === 2 ? (
                                    /* ═══ STEP 2: Choose Series ═══ */
                                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-3">שלב 2: בחר סדרה — {filterMfr} ({uniqueSeries.length})</p>
                                        <div className="grid grid-cols-2 gap-2">
                                            {uniqueSeries.map(s => {
                                                const count = mfrProducts.filter(p => p.series === s).length;
                                                const types = [...new Set(mfrProducts.filter(p => p.series === s).map(p => p.openingType).filter(Boolean))];
                                                return (
                                                    <button
                                                        type="button"
                                                        key={s}
                                                        onClick={() => setItemForm({...itemForm, _filterSeries: s})}
                                                        className="text-right p-3 rounded-xl transition-all border bg-white border-slate-100 hover:border-blue-300 hover:bg-blue-50 hover:shadow-sm group"
                                                    >
                                                        <p className="font-black text-slate-800 text-sm group-hover:text-blue-700">{s}</p>
                                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                                            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">{count} מוצרים</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-0.5 mt-1">
                                                            {types.slice(0, 3).map(t => (
                                                                <span key={t} className="text-[8px] text-slate-400">{t}</span>
                                                            ))}
                                                            {types.length > 3 && <span className="text-[8px] text-slate-400">+{types.length - 3}</span>}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* ═══ STEP 3: Filter & Choose Product ═══ */}
                                        <div className="p-3 space-y-2 border-b border-slate-100 text-right">
                                            {/* Category / Opening Type */}
                                            {uniqueCategories.length > 0 && (
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">סוג פתיחה</label>
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        <button type="button" onClick={() => setItemForm({...itemForm, _filterCat: ''})} className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${!filterCat ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>הכל</button>
                                                        {uniqueCategories.map(cat => (
                                                            <button type="button" key={cat} onClick={() => setItemForm({...itemForm, _filterCat: filterCat === cat ? '' : cat})} className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${filterCat === cat ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>{cat}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="flex gap-3 flex-wrap">
                                                {/* Wings */}
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">כנפיים</label>
                                                    <div className="flex gap-1 mt-1">
                                                        <button type="button" onClick={() => setItemForm({...itemForm, _filterWings: ''})} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${!filterWings ? 'bg-purple-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>הכל</button>
                                                        {uniqueWings.map(w => (
                                                            <button type="button" key={w} onClick={() => setItemForm({...itemForm, _filterWings: filterWings === String(w) ? '' : String(w)})} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${filterWings === String(w) ? 'bg-purple-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>{w}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Screen */}
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">רשת</label>
                                                    <div className="flex gap-1 mt-1">
                                                        {[['', 'הכל'], ['yes', '✓'], ['no', '✗']].map(([v, l]) => (
                                                            <button type="button" key={v} onClick={() => setItemForm({...itemForm, _filterScreen: filterScreen === v ? '' : v})} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${filterScreen === v ? 'bg-green-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>{l}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Shutter */}
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">תריס</label>
                                                    <div className="flex gap-1 mt-1">
                                                        {[['', 'הכל'], ['yes', '✓'], ['no', '✗']].map(([v, l]) => (
                                                            <button type="button" key={v} onClick={() => setItemForm({...itemForm, _filterShutter: filterShutter === v ? '' : v})} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${filterShutter === v ? 'bg-orange-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>{l}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Fixed / Static */}
                                                <div>
                                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">קבוע</label>
                                                    <div className="flex gap-1 mt-1">
                                                        {[['', 'הכל'], ['yes', '✓'], ['no', '✗']].map(([v, l]) => (
                                                            <button type="button" key={v} onClick={() => setItemForm({...itemForm, _filterFixed: filterFixed === v ? '' : v})} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${filterFixed === v ? 'bg-cyan-600 text-white' : 'bg-white text-slate-500 border border-slate-200'}`}>{l}</button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Product List */}
                                        <div className="flex-1 overflow-y-auto p-3 custom-scrollbar">
                                            <p className="text-[10px] text-slate-400 font-bold mb-2">{filteredProducts.length} מוצרים</p>
                                            <div className="space-y-1">
                                                {filteredProducts.map(p => (
                                                    <button type="button" key={p.cod} onClick={() => setItemForm({...itemForm, productCod: String(p.cod), model: p.description})} className={`w-full text-right p-3 rounded-xl text-sm transition-all ${selectedCod === String(p.cod) ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-white hover:bg-blue-50 text-slate-700 border border-slate-100 hover:border-blue-200'}`}>
                                                        <div className="flex justify-between items-center">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-slate-100 text-slate-400'}`}>{p.cod}</span>
                                                                {p.wingCount > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-purple-50 text-purple-600'}`}>{p.wingCount} כנפיים</span>}
                                                                {(p.description || '').includes('רשת') && <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-green-50 text-green-600'}`}>רשת</span>}
                                                                {((p.description || '').includes('תריס') || (p.description || '').includes('מונו')) && <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-orange-50 text-orange-600'}`}>תריס</span>}
                                                                {(p.fixedType && p.fixedType !== '') && <span className={`text-[10px] px-1.5 py-0.5 rounded ${selectedCod === String(p.cod) ? 'bg-blue-500' : 'bg-cyan-50 text-cyan-600'}`}>קבוע</span>}
                                                            </div>
                                                            <span className={`text-[10px] ${selectedCod === String(p.cod) ? 'text-blue-200' : 'text-slate-400'}`}>{p.openingType}</span>
                                                        </div>
                                                        <p className="font-bold mt-1 text-right leading-tight">{p.description}</p>
                                                    </button>
                                                ))}
                                                {filteredProducts.length === 0 && (
                                                    <div className="text-center py-8 text-slate-400">
                                                        <p className="text-sm font-bold">לא נמצאו מוצרים</p>
                                                        <p className="text-xs mt-1">נסה לשנות את הפילטרים</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            {/* RIGHT PANEL - Item Details */}
                            <div className="w-full md:w-1/2 flex flex-col overflow-y-auto custom-scrollbar">
                                <div className="p-6 space-y-4 flex-1">
                                    {selectedCod && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center justify-between">
                                            <div>
                                                <p className="text-xs font-bold text-blue-500">מוצר נבחר</p>
                                                <p className="text-sm font-black text-blue-700">{itemForm.model}</p>
                                            </div>
                                            <span className="text-xs font-mono bg-blue-100 text-blue-600 px-2 py-1 rounded">מקט {selectedCod}</span>
                                        </div>
                                    )}

                                    {(() => {
                                        const selectedProduct = selectedCod ? allProducts.find(p => String(p.cod) === selectedCod) : null;
                                        const productDesc = (selectedProduct?.description || itemForm.model || '').toLowerCase();
                                        const detectedType = selectedProduct?.openingType ? (TYPE_MAP[selectedProduct.openingType] || 'sliding') : 'sliding';
                                        const detectedWings = selectedProduct?.wingCount || parseInt((itemForm.title || '').match(/(\d)\s*כנפ/)?.[1]) || 2;
                                        const detectedScreen = productDesc.includes('רשת') || !!(itemForm.screen && itemForm.screen !== '');
                                        const detectedRollingShutter = productDesc.includes('מונו') || productDesc.includes('מונובלוק');
                                        const detectedSlidingShutter = (productDesc.includes('תריס') && !detectedRollingShutter) || !!(itemForm.shutter && itemForm.shutter !== '' && !detectedRollingShutter);
                                        let detectedFixed = 'none';
                                        const dbFixed = (selectedProduct?.fixedType || '');
                                        if (dbFixed.includes('תחתון') && dbFixed.includes('עליון')) detectedFixed = 'both';
                                        else if (dbFixed.includes('עליון')) detectedFixed = 'top';
                                        else if (dbFixed.includes('תחתון')) detectedFixed = 'bottom';
                                        else if (productDesc.includes('אמבטיה') || productDesc.includes('קבוע')) {
                                            if (productDesc.includes('עליון') && productDesc.includes('תחתון')) detectedFixed = 'both';
                                            else if (productDesc.includes('עליון')) detectedFixed = 'top';
                                            else detectedFixed = 'bottom';
                                        }
                                        const drawW = parseFloat(itemForm.width) || 120;
                                        const drawH = parseFloat(itemForm.height) || 140;
                                        const shutterBoxH = parseFloat(itemForm._shutterBoxHeight) || 28;
                                        const sirtutCode = selectedProduct?.sirtut;
                                        const fixedLabel = detectedFixed === 'both' ? 'עליון+תחתון' : detectedFixed === 'top' ? 'עליון' : 'תחתון';
                                        return (
                                            <div className="bg-gradient-to-br from-slate-50 to-blue-50/30 border border-slate-200 rounded-2xl p-4 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">תצוגה מקדימה</p>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        {detectedScreen && <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-bold">רשת</span>}
                                                        {detectedRollingShutter && <span className="text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-bold">מונובלוק</span>}
                                                        {detectedSlidingShutter && <span className="text-[9px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-bold">תריס הזזה</span>}
                                                        {detectedFixed !== 'none' && <span className="text-[9px] px-1.5 py-0.5 bg-cyan-100 text-cyan-700 rounded-full font-bold">קבוע {fixedLabel}</span>}
                                                        <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded-full font-bold">{detectedWings} כנפיים</span>
                                                    </div>
                                                </div>
                                                {detectedRollingShutter && (
                                                    <div className="flex items-center gap-2 bg-orange-50 rounded-lg px-3 py-1.5 border border-orange-200">
                                                        <label className="text-[10px] font-bold text-orange-600 whitespace-nowrap">גובה תיבת תריס</label>
                                                        <input type="number" value={itemForm._shutterBoxHeight || 28} onChange={e => setItemForm({...itemForm, _shutterBoxHeight: e.target.value})} className="w-16 px-2 py-0.5 rounded border border-orange-200 text-center text-xs font-mono bg-white outline-none" min={10} max={60} step={1} />
                                                        <span className="text-[10px] text-orange-400">ס״מ</span>
                                                    </div>
                                                )}
                                                <div className="flex gap-3 items-start">
                                                    <div className="flex-1 bg-white rounded-xl border border-slate-100 p-2 flex items-center justify-center" style={{minHeight: '160px'}}>
                                                        <WindowDrawing openingType={detectedType} wingCount={detectedWings} width={drawW} height={drawH} hasScreen={detectedScreen} hasRollingShutter={detectedRollingShutter} hasSlidingShutter={detectedSlidingShutter} fixedType={detectedFixed} shutterBoxHeight={shutterBoxH} />
                                                    </div>
                                                    {sirtutCode && (
                                                        <div className="w-20 flex flex-col items-center gap-1">
                                                            <img src={`/BASE/${sirtutCode}.JPG`} alt="base" className="w-20 h-20 object-contain rounded-lg border border-slate-200 bg-white p-1" onError={(e) => { e.target.onerror = null; e.target.src = `/BASE/${sirtutCode}.jpg`; }} />
                                                            <span className="text-[8px] text-slate-400 font-bold">BASE #{sirtutCode}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    {/* Title + Quantity */}
                                    <div className="flex gap-4">
                                        <div className="flex-[2]">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">תיאור / פריט *</label>
                                            <input required type="text" value={itemForm.title} onChange={e => {
                                                const newTitle = e.target.value;
                                                const modelMatch = newTitle.match(/\b([1-9]\d{3}[a-zA-Z]?)\b/);
                                                setItemForm({
                                                    ...itemForm, 
                                                    title: newTitle,
                                                    model: (modelMatch && !itemForm.productCod) ? modelMatch[1] : itemForm.model
                                                });
                                            }} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none focus:ring-2 focus:ring-blue-500 text-sm" placeholder="חלון ציר עליון 7000" />
                                        </div>
                                        <div className="w-20">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">כמות *</label>
                                            <input required type="number" min="1" value={itemForm.quantity} onChange={e => setItemForm({...itemForm, quantity: parseInt(e.target.value) || 1})} className="w-full p-2.5 rounded-xl border border-blue-200 bg-blue-50/50 text-blue-900 font-bold focus:bg-white outline-none focus:ring-2 focus:ring-blue-500 text-sm text-center" />
                                        </div>
                                    </div>

                                    {/* Index + Location */}
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">אינדקס</label>
                                            <input type="text" value={itemForm.indexNum} onChange={e => setItemForm({...itemForm, indexNum: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm" placeholder="W1" />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">מיקום</label>
                                            <input type="text" value={itemForm.location} onChange={e => setItemForm({...itemForm, location: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm" placeholder="סלון" />
                                        </div>
                                    </div>

                                    {/* Dimensions */}
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">רוחב (ס"מ)</label>
                                            <input type="text" value={itemForm.width} onChange={e => setItemForm({...itemForm, width: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none focus:ring-2 text-sm" placeholder="120" />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">גובה (ס"מ)</label>
                                            <input type="text" value={itemForm.height} onChange={e => setItemForm({...itemForm, height: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none focus:ring-2 text-sm" placeholder="140" />
                                        </div>
                                    </div>

                                    {/* Glass + Color */}
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">זכוכית</label>
                                            <select value={itemForm.glassId || ''} onChange={e => {
                                                const selected = pricingMeta.glassOptions.find(g => g.id === parseInt(e.target.value));
                                                setItemForm({...itemForm, glassId: e.target.value, glass: selected ? selected.description : ''});
                                            }} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm">
                                                <option value="">ללא זכוכית / בחירה חופשית...</option>
                                                {pricingMeta.glassOptions.map(g => <option key={g.id} value={g.id}>{g.description} ({g.priceSqM}₪)</option>)}
                                            </select>
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">צבע פרופיל</label>
                                            <select value={itemForm.colorId || ''} onChange={e => {
                                                const selected = pricingMeta.colorOptions.find(c => c.id === parseInt(e.target.value));
                                                setItemForm({...itemForm, colorId: e.target.value, color: selected ? selected.description : ''});
                                            }} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm">
                                                <option value="">רגיל / בחירה חופשית...</option>
                                                {pricingMeta.colorOptions.map(c => <option key={c.id} value={c.id}>{c.description}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Shutter + Screen */}
                                    <div className="flex gap-4">
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">תריס</label>
                                            <input type="text" value={itemForm.shutter} onChange={e => setItemForm({...itemForm, shutter: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm" placeholder="חשמלי שלב אור" />
                                        </div>
                                        <div className="flex-1">
                                            <label className="block text-xs font-bold text-slate-500 mb-1">רשת</label>
                                            <input type="text" value={itemForm.screen} onChange={e => setItemForm({...itemForm, screen: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm" placeholder="נגללת" />
                                        </div>
                                    </div>

                                    {/* Perzol */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1">פרזול חובה</label>
                                        <input type="text" value={itemForm.perzol} onChange={e => setItemForm({...itemForm, perzol: e.target.value})} className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm" placeholder="ידית משולבת..." />
                                    </div>

                                    {/* Notes */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1">הערות נוספות</label>
                                        <textarea value={itemForm.notes} onChange={e => setItemForm({...itemForm, notes: e.target.value})} rows="2" className="w-full p-2.5 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white outline-none text-sm"></textarea>
                                    </div>

                                    {/* Image Upload */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-500 mb-1">העלאת שרטוט / צילום מסך לפריט (אופציונלי)</label>
                                        {itemForm.image ? (
                                            <div className="flex items-center gap-4 bg-green-50 text-green-700 p-3 rounded-xl text-sm">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <a href={itemForm.image} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-green-900">
                                                    צפה בתמונה
                                                </a>
                                                <button type="button" onClick={() => setItemForm({...itemForm, image: ''})} className="text-red-500 font-bold ml-auto px-2">X מחיקה</button>
                                            </div>
                                        ) : (
                                            <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full p-2.5 border border-slate-200 border-dashed rounded-xl text-sm text-slate-500 file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                        )}
                                    </div>
                                </div>
                                
                                {/* Action Buttons - Sticky Bottom */}
                                <div className="p-5 flex gap-3 border-t border-slate-100 bg-white flex-shrink-0">
                                    <button type="submit" className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold py-3 px-4 rounded-xl transition-all shadow-lg shadow-blue-200">
                                        {editingIndex !== null ? 'עדכן פריט' : 'שמור פריט'}
                                    </button>
                                    <button type="button" onClick={closeItemModal} className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold py-3 px-6 rounded-xl transition-colors">
                                        ביטול
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                );
            })()}

            {/* Quote / Bill of Materials Modal */}
            {quoteData && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-2xl p-8 shadow-2xl">
                        <div className="flex justify-between items-center mb-6 border-b border-slate-100 pb-4">
                            <div>
                                <h2 className="text-2xl font-black text-slate-800">הצעת מחיר מפורטת</h2>
                                <p className="text-slate-500 text-sm mt-1">
                                    {quoteData.report.product?.description} — {quoteData.report.product?.manufacturer} {quoteData.report.product?.series}
                                </p>
                            </div>
                            <button type="button" onClick={() => setQuoteData(null)} className="text-slate-400 hover:bg-slate-100 w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                                &times;
                            </button>
                        </div>
                        
                        <div className="space-y-5 max-h-[65vh] overflow-y-auto px-2 custom-scrollbar">
                            {quoteData.report.bom.filter(b => b.type === 'profile').length > 0 && (
                                <div>
                                    <h3 className="font-bold text-slate-700 mb-3 border-r-4 border-blue-500 pr-2">פרופילי אלומיניום (לפי משקל)</h3>
                                    <div className="space-y-1.5">
                                        {quoteData.report.bom.filter(b => b.type === 'profile').map((b, i) => (
                                            <div key={i} className="flex items-center gap-3 text-sm bg-slate-50 p-3 rounded-lg border border-slate-100">
                                                <img 
                                                    src={`/catalog_images/C${String(b.makat).padStart(5, '0')}.jpg`} 
                                                    alt={b.name}
                                                    className="w-10 h-10 object-contain rounded bg-white border border-slate-200 flex-shrink-0"
                                                    onError={(e) => { 
                                                        if (!e.target.dataset.fallback) {
                                                            e.target.dataset.fallback = '1';
                                                            e.target.src = `/profiles/${String(b.makat).padStart(5, '0')}.jpg`;
                                                        } else {
                                                            e.target.style.display = 'none';
                                                        }
                                                    }}
                                                />
                                                <span className="font-bold text-slate-700 flex-1">{b.name} <span className="text-slate-400 font-normal text-xs mr-1">(מקט: {b.makat})</span></span>
                                                <span className="text-slate-600 font-medium bg-white px-3 py-1 rounded shadow-sm text-xs whitespace-nowrap">
                                                    {b.lengthMm} מ"מ &times; {b.qty} = {(b.weightGr / 1000).toFixed(2)} ק"ג
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {quoteData.report.bom.filter(b => b.type === 'dimension').length > 0 && (
                                <div>
                                    <h3 className="font-bold text-slate-700 mb-3 border-r-4 border-purple-500 pr-2">מידות חיתוך</h3>
                                    <div className="space-y-1.5">
                                        {quoteData.report.bom.filter(b => b.type === 'dimension').map((b, i) => (
                                            <div key={i} className="flex justify-between items-center text-sm bg-purple-50 p-2.5 rounded-lg border border-purple-100">
                                                <span className="font-bold text-purple-700">{b.name}</span>
                                                <span className="text-purple-600 font-medium">{b.lengthMm} מ"מ &times; {b.qty} יח'</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {quoteData.report.bom.filter(b => b.type === 'glass').length > 0 && (
                                <div>
                                    <h3 className="font-bold text-slate-700 mb-3 border-r-4 border-cyan-500 pr-2">זכוכית</h3>
                                    {quoteData.report.bom.filter(b => b.type === 'glass').map((b, i) => (
                                        <div key={i} className="bg-cyan-50 p-4 rounded-lg border border-cyan-100 space-y-2">
                                            <div className="flex justify-between items-center">
                                                <span className="font-bold text-cyan-800">{b.name}</span>
                                                <span className="font-black text-cyan-700 text-lg">{b.cost} ₪</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                <div className="bg-white p-2 rounded border border-cyan-100">
                                                    <span className="text-slate-400 block">מידת חיתוך לכנף</span>
                                                    <span className="font-bold text-cyan-700" dir="ltr">{b.glassWidthMm} × {b.glassHeightMm} mm</span>
                                                </div>
                                                <div className="bg-white p-2 rounded border border-cyan-100">
                                                    <span className="text-slate-400 block">כמות כנפיים</span>
                                                    <span className="font-bold text-cyan-700">{b.qtyWings}</span>
                                                </div>
                                                <div className="bg-white p-2 rounded border border-cyan-100">
                                                    <span className="text-slate-400 block">שטח למ"ר</span>
                                                    <span className="font-bold text-cyan-700">{b.areaSqMPerWing} מ"ר × {b.qtyWings} = {b.areaSqM} מ"ר</span>
                                                </div>
                                                <div className="bg-white p-2 rounded border border-cyan-100">
                                                    <span className="text-slate-400 block">מחיר למ"ר</span>
                                                    <span className="font-bold text-cyan-700">{b.pricePerSqM} ₪/מ"ר</span>
                                                </div>
                                            </div>
                                            {b.fallback && <p className="text-[10px] text-amber-600 font-bold">⚠ מידות חיתוך לא נמצאו בנוסחאות — חישוב לפי מידות הפתח</p>}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {quoteData.report.bom.filter(b => b.type === 'hardware').length > 0 && (
                                <div>
                                    <h3 className="font-bold text-slate-700 mb-3 border-r-4 border-amber-500 pr-2">
                                        פרזול / חומרה
                                        <span className="text-xs text-slate-400 font-normal mr-2">
                                            ({quoteData.report.bom.filter(b => b.type === 'hardware').length} פריטים — סה"כ {Math.round(quoteData.report.totals.hardwareCost)} ₪)
                                        </span>
                                    </h3>
                                    <div className="max-h-48 overflow-y-auto space-y-1">
                                        {quoteData.report.bom.filter(b => b.type === 'hardware').map((b, i) => (
                                            <div key={i} className="flex items-center gap-2 text-xs bg-amber-50 px-3 py-1.5 rounded border border-amber-100">
                                                <img 
                                                    src={`/perzol_images/${b.pnum || b.misp || ''}.jpg`}
                                                    alt={b.name}
                                                    className="w-8 h-8 object-contain rounded bg-white border border-amber-200 flex-shrink-0"
                                                    onError={(e) => { e.target.style.display = 'none'; }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <span className="font-semibold text-amber-800 truncate block">{b.name}</span>
                                                    {b.pnum && <span className="text-[9px] text-amber-400">מקט: {b.pnum}</span>}
                                                    {b.fallback && <span className="text-[9px] text-amber-500">(ברירת מחדל)</span>}
                                                </div>
                                                <div className="flex items-center gap-3 text-amber-700 flex-shrink-0">
                                                    <span>{b.qty} {b.unit}</span>
                                                    <span>× {b.unitPrice}₪</span>
                                                    <span className="font-bold min-w-[50px] text-left">{b.cost} ₪</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-slate-200">
                                <h3 className="font-bold text-slate-700 mb-3 border-r-4 border-green-500 pr-2">פירוט עלויות</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                                        <span className="text-slate-600">אלומיניום ({quoteData.report.totals.aluminumWeightKg} ק"ג × {quoteData.report.totals.alumPricePerKg} ₪/ק"ג)
                                            {quoteData.report.totals.colorName && <span className="text-xs text-slate-400 mr-1">({quoteData.report.totals.colorName})</span>}
                                        </span>
                                        <span className="font-bold">{quoteData.report.totals.aluminumCost} ₪</span>
                                    </div>
                                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                                        <span className="text-slate-600">זכוכית
                                            {quoteData.report.bom.find(b => b.type === 'glass') && 
                                                <span className="text-xs text-slate-400 mr-1">
                                                    ({quoteData.report.bom.find(b => b.type === 'glass').areaSqM} מ"ר × {quoteData.report.bom.find(b => b.type === 'glass').pricePerSqM} ₪/מ"ר)
                                                </span>
                                            }
                                        </span>
                                        <span className="font-bold">{quoteData.report.totals.glassCost} ₪</span>
                                    </div>
                                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                                        <span className="text-slate-600">פרזול / חומרה</span>
                                        <span className="font-bold">{quoteData.report.totals.hardwareCost} ₪</span>
                                    </div>
                                    <div className="flex justify-between p-2 bg-green-50 rounded border border-green-100 font-bold">
                                        <span className="text-green-700">סה"כ חומרים</span>
                                        <span className="text-green-700">{quoteData.report.totals.materialsCost} ₪</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between p-2 bg-orange-50 rounded border border-orange-100">
                                    <span className="text-orange-700 font-bold">הרכבת חלון/דלת (ייצור)</span>
                                    <span className="font-bold text-orange-700">{quoteData.report.totals.assemblyCost} ₪</span>
                                </div>
                                <div className="flex justify-between p-2 bg-orange-50 rounded border border-orange-100">
                                    <span className="text-orange-700 font-bold">התקנה בשטח ({quoteData.report.totals.installationPct}%)</span>
                                    <span className="font-bold text-orange-700">{quoteData.report.totals.installationCost} ₪</span>
                                </div>
                                <div className="flex justify-between p-2 bg-amber-50 rounded border border-amber-200">
                                    <span className="text-amber-700 font-bold">אחוז רווח ({quoteData.report.totals.revenuePct}%)</span>
                                    <span className="font-bold text-amber-700">{quoteData.report.totals.finalPrice - quoteData.report.totals.totalBeforeRevenue} ₪</span>
                                </div>
                            </div>

                            <div className="bg-blue-50 p-5 rounded-xl border border-blue-200 shadow-inner flex justify-between items-center mt-4">
                                <div>
                                    <h4 className="text-[10px] uppercase tracking-widest font-black text-blue-500 mb-0.5">מחיר סופי ללקוח</h4>
                                    <p className="text-xs text-blue-400">כולל חומרים + ייצור + התקנה + רווח</p>
                                </div>
                                <p className="text-3xl font-black text-blue-700">{quoteData.report.totals.finalPrice} ₪</p>
                            </div>

                            {quoteData.report.bom.length === 0 && <p className="text-sm text-slate-400 text-center py-4">לא נמצאו נוסחאות/ פרופילים לדגם זה במסד ה-ERP. המחיר מבוסס על עלויות ברירת מחדל בלבד.</p>}
                        </div>
                    </div>
                </div>
            )}
            </div>
            </div>
    );
}
